/** @jsxImportSource @emotion/react */

import { ChampionshipEditTab } from 'modules/championship/domain/enums/championship-edit-tab';
import { ChampionshipStageTab } from 'modules/championship/domain/enums/championship-stage-tab';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { announcedChampionshipTeamsDataSelector } from 'modules/teams/store/championship-teams/selectors';
import { championshipIsLoadingSelector } from 'modules/championship/store/championship/selectors';
import React, { useEffect, useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useSelector } from 'react-redux';
import { useMatch, useParams } from 'react-router-dom';
import { useFeatureFlag } from 'shared/feature-flags';
import { RootState } from 'store/store';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import ChampionshipEditForm from 'modules/championship/ui/components/edit-form';
import ChampionshipEditFormTabs from 'modules/championship/ui/components/edit-form/tabs';
import { getStageTabLabel } from 'modules/championship/ui/components/form-parts/stages/constants';
import { stageSelectors } from 'modules/championship/store/stages';
import { useChampionship } from 'modules/championship/ui/hooks/use-championship';
import { useChampionshipFetch } from 'modules/championship/ui/hooks/use-championship-fetch';

const ChampionshipEditPage = () => {
  const { id: champId } = useParams();
  useChampionshipFetch();

  const { t } = useLingui();
  const isFlow1447 = useFeatureFlag('applicationsFlow1447');
  const { data: champ } = useChampionship(Number(champId));
  const champTeamsData = useSelector(announcedChampionshipTeamsDataSelector(champ?.id));
  const isChampionshipLoading = useSelector(championshipIsLoadingSelector);
  const { setHeaderContent, setContentClassName } = useMainLayout();

  const applicationsPageMatch = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/applications/*`);
  const applicationPlayersPageMatch = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/applications/:teamId`);
  const teamPlayersPageRules = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/rules`);
  // `/*` обязателен: без него крошка «Этапы» пропадала бы на любой вложенной странице.
  const teamPlayersPageSteps = useMatch(`${Pages.CHAMPIONSHIPS.STEPS.INDEX}/*`);
  const teamPlayersPageSymbolicTeams = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/symbolic-teams`);

  const stageCreateMatch = useMatch(Pages.CHAMPIONSHIPS.STEPS.CREATE);
  const stageMatch = useMatch(`${Pages.CHAMPIONSHIPS.STEPS.STAGE}/*`);
  const stageTeamsMatch = useMatch(`${Pages.CHAMPIONSHIPS.STEPS.TEAMS}/*`);
  const stageGridMatch = useMatch(`${Pages.CHAMPIONSHIPS.STEPS.GRID}/*`);
  const stageMatchesMatch = useMatch(`${Pages.CHAMPIONSHIPS.STEPS.MATCHES}/*`);

  // `/steps/create` подходит под шаблон `:stageId`, поэтому сначала отсекаем создание.
  const routeStageId = Number(stageMatch?.params.stageId);
  const isStageRoute = !stageCreateMatch && Number.isInteger(routeStageId) && routeStageId > 0;
  const routeStage = useSelector((state: RootState) => (isStageRoute ? stageSelectors.selectById(state, routeStageId) : undefined));

  const stageTab = stageMatchesMatch
    ? ChampionshipStageTab.MATCHES
    : stageGridMatch
      ? ChampionshipStageTab.GRID
      : stageTeamsMatch
        ? ChampionshipStageTab.TEAMS
        : null;

  const breadcrumbsItems = useMemo(() => {
    const items = [{ label: t`Турниры`, to: Pages.CHAMPIONSHIPS.INDEX }];

    if (!champ) {
      if (isChampionshipLoading) {
        items.push({
          label: t`Загрузка...`,
          to: Pages.CHAMPIONSHIPS.builders.edit(Number(champId)),
        });
      }
      return items;
    }

    items.push({
      label: champ.name,
      to: Pages.CHAMPIONSHIPS.builders.edit(champ.id),
    });

    if (applicationsPageMatch) {
      items.push({
        label: isFlow1447 ? t`Команды` : t`Заявки`,
        to: Pages.CHAMPIONSHIPS.builders.edit(champ.id, ChampionshipEditTab.TEAMS),
      });
    }

    if (teamPlayersPageRules) {
      items.push({
        label: isFlow1447 ? t`Параметры` : t`Условия`,
        to: Pages.CHAMPIONSHIPS.builders.edit(champ.id, 'rules'),
      });
    }

    if (teamPlayersPageSteps) {
      items.push({
        label: t`Этапы`,
        to: Pages.CHAMPIONSHIPS.STEPS.builders.index(champ.id),
      });

      if (stageCreateMatch) {
        items.push({
          label: t`Добавление этапа`,
          to: Pages.CHAMPIONSHIPS.STEPS.builders.create(champ.id),
        });
      } else if (routeStage) {
        items.push({
          label: routeStage.name,
          to: Pages.CHAMPIONSHIPS.STEPS.builders.stage(champ.id, routeStage.id),
        });

        // Глубже (Таблица/Шахматка, страница матча) не идём: эти уровни подписаны
        // прямо на экране, а шестая крошка делает шапку нечитаемой.
        if (stageTab) {
          items.push({
            label: t(getStageTabLabel(stageTab)),
            to: Pages.CHAMPIONSHIPS.STEPS.builders.stage(champ.id, routeStage.id, stageTab),
          });
        }
      }
    }

    if (teamPlayersPageSymbolicTeams) {
      items.push({
        label: t`Сборные`,
        to: Pages.CHAMPIONSHIPS.builders.edit(champ.id, 'symbolic-teams'),
      });
    }

    if (applicationPlayersPageMatch && applicationPlayersPageMatch.params.teamId && champTeamsData) {
      const teamId = parseInt(applicationPlayersPageMatch.params.teamId);
      const champTeam = champTeamsData[teamId];

      if (champTeam) {
        items.push({
          label: champTeam.name,
          to: Pages.CHAMPIONSHIPS.builders.edit(champ.id, `${ChampionshipEditTab.TEAMS}/${champTeam.id}`),
        });
      }
    }

    return items;
  }, [
    champ,
    applicationsPageMatch,
    teamPlayersPageRules,
    teamPlayersPageSteps,
    teamPlayersPageSymbolicTeams,
    applicationPlayersPageMatch,
    champTeamsData,
    isChampionshipLoading,
    champId,
    stageCreateMatch,
    routeStage,
    stageTab,
    t,
    isFlow1447,
  ]);

  const breadcrumbs = useMemo(() => {
    return <UDBreadcrumbs className="my-10" breadcrumbs={breadcrumbsItems} />;
  }, [breadcrumbsItems]);

  useEffect(() => {
    setContentClassName('flex flex-col');
    setHeaderContent(breadcrumbs);

    return () => {
      setContentClassName(null);
      setHeaderContent(null);
    };
  }, [breadcrumbs, setContentClassName, setHeaderContent]);

  return (
    <>
      <div className="w-full px-6 py-6">
        <ChampionshipEditFormTabs championship={champ} />
      </div>
      <ChampionshipEditForm championship={champ} />
    </>
  );
};

export default ChampionshipEditPage;
